(function () {
	"use strict";

	if (window.shopCurrency !== "USD") return;

	const ROOT = document.documentElement;
	const INVALID_MESSAGE =
		"Match Paint by VIN requires a valid 17-character VIN for every matching item. Remove the affected item and add it again with its VIN before checkout.";
	const VALIDATION_ERROR_MESSAGE =
		"We could not verify the VIN details in your cart. Please try again before checkout.";
	const PRODUCT_BOUNDARY_SELECTOR = "form.partify-vin-quickshop-boundary";
	const approvedClicks = new WeakSet();
	const approvedSubmits = new WeakSet();
	const nativeSubmit = HTMLFormElement.prototype.submit;
	let latestValidation = null;
	let refreshSequence = 0;

	function normalizeVin(value) {
		const vin = String(value || "").trim().toUpperCase();
		if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(vin)) return "";
		const values = {
			A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8,
			J: 1, K: 2, L: 3, M: 4, N: 5, P: 7, R: 9,
			S: 2, T: 3, U: 4, V: 5, W: 6, X: 7, Y: 8, Z: 9,
		};
		const weights = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];
		let sum = 0;
		for (let index = 0; index < vin.length; index++) {
			sum += (values[vin[index]] ?? Number(vin[index])) * weights[index];
		}
		const remainder = sum % 11;
		return vin[8] === (remainder === 10 ? "X" : String(remainder)) ? vin : "";
	}

	function isVinSku(value) {
		return /(?:\.VIN$|_VIN_)/i.test(String(value || "").trim());
	}

	function readActiveVehicle() {
		try {
			const terms = typeof window.getSearchTerms === "function"
				? window.getSearchTerms()
				: JSON.parse(localStorage.getItem("searchTerms") || "[]");
			return Array.isArray(terms) && terms[0] ? terms[0] : null;
		} catch (error) {
			return null;
		}
	}

	function activeDecoderVinInput() {
		try {
			if (typeof window.getVisibleVinDecoderElements === "function") {
				const visible = window.getVisibleVinDecoderElements();
				if (visible && visible.input) return visible.input;
			}
		} catch (error) {}

		const inputs = Array.from(
			document.querySelectorAll('input[id^="vin-decoder-textbox-for-verification-"]'),
		);
		return inputs.find(function (input) {
			const wrapper = input.closest('[id^="vin-decoder-textbox-for-verification-wrapper"]');
			return (
				input.offsetParent !== null ||
				(wrapper && wrapper.classList.contains("show") && wrapper.style.display !== "none")
			);
		}) || (inputs.length === 1 ? inputs[0] : null);
	}

	function resolveProductVinContext(form) {
		const activeVehicle = readActiveVehicle();
		const acceptedVin = normalizeVin(activeVehicle && activeVehicle.vin);
		const decoderInput = activeDecoderVinInput();
		const decoderRaw = String((decoderInput && decoderInput.value) || "").trim().toUpperCase();
		const decoderVin = normalizeVin(decoderRaw);
		const manualInput = document.getElementById("vin-after-paint-code-textbox");
		const manualRaw = String((manualInput && manualInput.value) || "").trim().toUpperCase();
		const manualVin = normalizeVin(manualRaw);

		let conflict = false;
		let reason = "";
		if (decoderRaw && (!acceptedVin || decoderVin !== acceptedVin)) {
			conflict = true;
			reason = "pending-decoder-vin";
		} else if (manualRaw && !manualVin) {
			conflict = true;
			reason = "invalid-manual-vin";
		} else if (acceptedVin && manualVin && manualVin !== acceptedVin) {
			conflict = true;
			reason = "manual-vin-mismatch";
		}

		return {
			vin: conflict ? "" : acceptedVin || manualVin,
			acceptedVin: acceptedVin,
			manualVin: manualVin,
			decoderVin: decoderVin,
			decoderInput: decoderInput,
			conflict: conflict,
			reason: reason,
		};
	}

	function canonicalVinInput(form) {
		if (!form) return null;
		let input = form.querySelector('input[type="hidden"][data-partify-canonical-vin]');
		if (!input) {
			input = form.querySelector('input[type="hidden"][name="properties[VIN]"]');
		}
		if (!input) {
			input = document.createElement("input");
			input.type = "hidden";
			input.name = "properties[VIN]";
			form.appendChild(input);
		}
		input.setAttribute("data-partify-canonical-vin", "true");
		return input;
	}

	function syncProductVin(form) {
		const productForm = form || document.getElementById("product-form");
		if (!productForm) return resolveProductVinContext(productForm);
		const context = resolveProductVinContext(productForm);
		const canonical = canonicalVinInput(productForm);
		if (canonical) {
			canonical.value = context.vin;
			canonical.disabled = !context.vin;
		}
		return context;
	}

	function syncAcceptedVinToPdp() {
		const productForm = document.getElementById("product-form");
		const acceptedVin = normalizeVin(readActiveVehicle() && readActiveVehicle().vin);
		const input = activeDecoderVinInput();
		if (input) {
			const raw = String(input.value || "").trim().toUpperCase();
			const priorAccepted = String(input.dataset.partifyAcceptedVin || "");
			const deliberatelyEditing = input.dataset.partifyVinDirty === "true";
			const acceptedVehicleChanged = acceptedVin !== priorAccepted;
			if (acceptedVehicleChanged || (!deliberatelyEditing && (!raw || raw === priorAccepted || raw === acceptedVin))) {
				input.value = acceptedVin;
				input.dataset.partifyAcceptedVin = acceptedVin;
				if (acceptedVehicleChanged) delete input.dataset.partifyVinDirty;
			}
		}
		syncProductVin(productForm);
	}

	function prepareNativeSubmit(form) {
		const productForm = form || document.getElementById("product-form");
		const context = syncProductVin(productForm);
		if (!productForm || context.conflict) return context;
		const canonical = canonicalVinInput(productForm);
		productForm.querySelectorAll('input[name="properties[VIN]"],input[name="properties[Vin]"]').forEach(function (input) {
			if (input !== canonical) input.disabled = true;
		});
		return context;
	}

	function initializeProductVinSync() {
		const productForm = document.getElementById("product-form");
		if (!productForm || productForm.dataset.partifyVinSyncBound === "true") return;
		productForm.dataset.partifyVinSyncBound = "true";
		document.addEventListener("input", function (event) {
			const target = event.target;
			if (!target || !target.matches) return;
			if (target.matches('input[id^="vin-decoder-textbox-for-verification-"]')) {
				target.dataset.partifyVinDirty = "true";
				syncProductVin(productForm);
			} else if (target.id === "vin-after-paint-code-textbox") {
				syncProductVin(productForm);
			}
		}, true);
		window.addEventListener("searchTermsUpdated", syncAcceptedVinToPdp);
		window.addEventListener("partify:vehicle:changed", syncAcceptedVinToPdp);
		syncAcceptedVinToPdp();
	}

	function validateCart(cart) {
		const items = Array.isArray(cart && cart.items) ? cart.items : [];
		const invalidItems = items.filter(function (item) {
			if (!isVinSku(item && item.sku)) return false;
			return !normalizeVin(item && item.properties && item.properties.VIN);
		});
		return { cart: cart, invalidItems: invalidItems, valid: invalidItems.length === 0 };
	}

	function ensureStyles() {
		if (document.getElementById("partify-vin-cart-guard-styles")) return;
		const style = document.createElement("style");
		style.id = "partify-vin-cart-guard-styles";
		style.textContent =
			"html.partify-vin-cart-pending .additional-checkout-buttons," +
			"html.partify-vin-cart-invalid .additional-checkout-buttons{display:none!important}" +
			"html.partify-vin-cart-pending a[href*=\"checkout\"]," +
			"html.partify-vin-cart-pending button[name=\"checkout\"]," +
			"html.partify-vin-cart-pending [data-sticky-checkout-btn]{visibility:hidden!important}" +
			"form.partify-vin-quickshop-boundary.partify-vin-selection-blocked .shopify-payment-button{display:none!important}" +
			".partify-vin-cart-error,.partify-vin-product-error{color:#a12622;font-weight:600;margin:12px 0}";
		document.head.appendChild(style);
	}

	function cartMessageHosts() {
		const selectors = [
			".cart-drawer__footer",
			".carid-summary-checkout",
			".cart-checkout",
		];
		return Array.from(document.querySelectorAll(selectors.join(",")));
	}

	function setCartMessage(message) {
		cartMessageHosts().forEach(function (host) {
			let messageElement = host.querySelector(":scope > .partify-vin-cart-error");
			if (!messageElement) {
				messageElement = document.createElement("p");
				messageElement.className = "partify-vin-cart-error";
				messageElement.setAttribute("role", "alert");
				host.prepend(messageElement);
			}
			messageElement.textContent = message || "";
			messageElement.hidden = !message;
		});
	}

	function setCartState(state, message) {
		ROOT.classList.toggle("partify-vin-cart-pending", state === "pending");
		ROOT.classList.toggle("partify-vin-cart-invalid", state === "invalid");
		setCartMessage(message || "");
	}

	async function readAuthoritativeCart() {
		let timeoutId = null;
		try {
			const response = await Promise.race([
				fetch("/cart.js", {
					cache: "no-store",
					credentials: "same-origin",
					headers: { Accept: "application/json", "Cache-Control": "no-cache" },
				}),
				new Promise(function (resolve, reject) {
					timeoutId = window.setTimeout(function () {
						reject(new Error("Cart validation request timed out"));
					}, 3500);
				}),
			]);
			if (!response.ok) throw new Error("Cart validation request failed");
			return response.json();
		} finally {
			if (timeoutId !== null) window.clearTimeout(timeoutId);
		}
	}

	async function refresh() {
		const sequence = ++refreshSequence;
		setCartState("pending", "");
		try {
			const validation = validateCart(await readAuthoritativeCart());
			if (sequence !== refreshSequence) return latestValidation;
			latestValidation = validation;
			setCartState(validation.valid ? "valid" : "invalid", validation.valid ? "" : INVALID_MESSAGE);
			return validation;
		} catch (error) {
			if (sequence !== refreshSequence) return latestValidation;
			latestValidation = { cart: null, error: error, invalidItems: [], valid: false };
			setCartState("invalid", VALIDATION_ERROR_MESSAGE);
			return latestValidation;
		}
	}

	async function requestCheckout(continuation) {
		const validation = await refresh();
		if (!validation || !validation.valid) return false;
		if (typeof continuation === "function") continuation();
		else if (continuation) window.location.assign(String(continuation));
		return true;
	}

	function checkoutLink(element) {
		const link = element && element.closest ? element.closest("a[href]") : null;
		if (!link) return null;
		try {
			const url = new URL(link.href, window.location.href);
			if (url.pathname === "/checkout") return link;
			const redirect = url.searchParams.get("redirect");
			return redirect && new URL(redirect, window.location.origin).pathname === "/checkout" ? link : null;
		} catch (error) {
			return null;
		}
	}

	function checkoutButton(element) {
		return element && element.closest
			? element.closest('button[name="checkout"],[data-sticky-checkout-btn]')
			: null;
	}

	function isCheckoutSubmit(event) {
		if (checkoutButton(event.submitter)) return true;
		const form = event.target;
		if (!(form instanceof HTMLFormElement)) return false;
		try {
			return new URL(form.action, window.location.href).pathname === "/checkout";
		} catch (error) {
			return false;
		}
	}

	function variantsForForm(form) {
		const data = form.querySelector("[data-partify-vin-variants]");
		if (!data) return [];
		try {
			const variants = JSON.parse(data.textContent || "[]");
			return Array.isArray(variants) ? variants : [];
		} catch (error) {
			return [];
		}
	}

	function selectedVariant(form) {
		const idInput = form.querySelector('[name="id"]');
		const selectedId = idInput && !idInput.disabled ? String(idInput.value || "") : "";
		return variantsForForm(form).find(function (variant) {
			return String(variant && variant.id) === selectedId;
		}) || null;
	}

	function showProductBoundaryError(form) {
		let error = form.querySelector(".partify-vin-product-error");
		if (!error) {
			error = document.createElement("p");
			error.className = "partify-vin-product-error";
			error.setAttribute("role", "alert");
			const productUrl = form.getAttribute("data-partify-product-url") || "";
			error.textContent = "Match Paint by VIN requires a valid 17-character VIN. ";
			if (productUrl) {
				const link = document.createElement("a");
				link.href = productUrl;
				link.textContent = "Open the product page to enter it.";
				error.appendChild(link);
			}
			form.appendChild(error);
		}
		error.hidden = false;
	}

	function syncProductBoundary(form) {
		const variant = selectedVariant(form);
		const blocked = isVinSku(variant && variant.sku);
		form.classList.toggle("partify-vin-selection-blocked", blocked);
		const error = form.querySelector(".partify-vin-product-error");
		if (error && !blocked) error.hidden = true;
		return blocked;
	}

	function syncAllProductBoundaries(root) {
		const scope = root && root.querySelectorAll ? root : document;
		scope.querySelectorAll(PRODUCT_BOUNDARY_SELECTOR).forEach(syncProductBoundary);
		if (root instanceof HTMLFormElement && root.matches(PRODUCT_BOUNDARY_SELECTOR)) {
			syncProductBoundary(root);
		}
	}

	document.addEventListener(
		"click",
		function (event) {
			const target = checkoutButton(event.target) || checkoutLink(event.target);
			if (!target) return;
			if (approvedClicks.has(target)) {
				approvedClicks.delete(target);
				return;
			}
			event.preventDefault();
			event.stopImmediatePropagation();
			requestCheckout(function () {
				approvedClicks.add(target);
				const form = target.form;
				if (form) approvedSubmits.add(form);
				target.click();
			});
		},
		true,
	);

	document.addEventListener(
		"submit",
		function (event) {
			const form = event.target;
			if (!(form instanceof HTMLFormElement)) return;
			if (form.matches(PRODUCT_BOUNDARY_SELECTOR) && syncProductBoundary(form)) {
				event.preventDefault();
				event.stopImmediatePropagation();
				showProductBoundaryError(form);
				return;
			}
			if (!isCheckoutSubmit(event)) return;
			if (approvedSubmits.has(form)) {
				approvedSubmits.delete(form);
				return;
			}
			event.preventDefault();
			event.stopImmediatePropagation();
			const submitter = event.submitter;
			requestCheckout(function () {
				approvedSubmits.add(form);
				if (submitter && typeof form.requestSubmit === "function") form.requestSubmit(submitter);
				else nativeSubmit.call(form);
			});
		},
		true,
	);

	HTMLFormElement.prototype.submit = function () {
		const form = this;
		if (form.matches(PRODUCT_BOUNDARY_SELECTOR) && syncProductBoundary(form)) {
			showProductBoundaryError(form);
			return;
		}
		let checkoutAction = false;
		try {
			checkoutAction = new URL(form.action, window.location.href).pathname === "/checkout";
		} catch (error) {
			checkoutAction = false;
		}
		if (!checkoutAction) {
			nativeSubmit.call(form);
			return;
		}
		requestCheckout(function () {
			nativeSubmit.call(form);
		});
	};

	document.addEventListener("change", function (event) {
		const form = event.target && event.target.closest
			? event.target.closest(PRODUCT_BOUNDARY_SELECTOR)
			: null;
		if (form) syncProductBoundary(form);
	}, true);

	["cart:refresh", "cart:update"].forEach(function (eventName) {
		document.addEventListener(eventName, refresh);
	});
	window.addEventListener("cartcount:update", refresh);

	const observer = new MutationObserver(function (mutations) {
		// Discover added forms once per delivery. Unrelated widget updates do not
		// need a selector scan, and nested insertions must not validate a form twice.
		const roots = new Set();
		mutations.forEach(function (mutation) {
			mutation.addedNodes.forEach(function (node) {
				if (node.nodeType === Node.ELEMENT_NODE && node.isConnected) roots.add(node);
			});
		});
		const forms = new Set();
		roots.forEach(function (root) {
			let parent = root.parentElement;
			while (parent) {
				if (roots.has(parent)) return;
				parent = parent.parentElement;
			}
			if (root instanceof HTMLFormElement && root.matches(PRODUCT_BOUNDARY_SELECTOR)) {
				forms.add(root);
			}
			if (root.firstElementChild) {
				Array.from(root.getElementsByTagName("form")).forEach(function (form) {
					if (form.matches(PRODUCT_BOUNDARY_SELECTOR)) forms.add(form);
				});
			}
		});
		forms.forEach(syncProductBoundary);
	});

	ensureStyles();
	syncAllProductBoundaries(document);
	observer.observe(document.documentElement, { childList: true, subtree: true });
	window.PartifyVinCartGuard = {
		getAcceptedVehicleVin: function () {
			return normalizeVin(readActiveVehicle() && readActiveVehicle().vin);
		},
		isVinSku: isVinSku,
		normalizeVin: normalizeVin,
		prepareNativeSubmit: prepareNativeSubmit,
		refresh: refresh,
		requestCheckout: requestCheckout,
		resolveProductVinContext: resolveProductVinContext,
		syncProductVin: syncProductVin,
		validateCart: validateCart,
	};
	initializeProductVinSync();
	refresh()
		.then(function () {
			window.__partifyVinCartGuardReady = true;
			if (window.__partifyVinCartGuardBootstrapTimer) {
				window.clearTimeout(window.__partifyVinCartGuardBootstrapTimer);
			}
		})
		.catch(function () {
			if (typeof window.__partifyVinCartGuardReleaseBootstrap === "function") {
				window.__partifyVinCartGuardReleaseBootstrap();
			} else {
				ROOT.classList.remove("partify-vin-cart-pending", "partify-vin-cart-invalid");
			}
		});
})();
